/*
 * FORG3D Cart Drawer — vanilla JS handler.
 *
 * Listens for `forg3d:cart-open` (fired by forg3d-header cart icon).
 * Intercepts any form posting to /cart/add via AJAX, refreshes drawer
 * contents from /cart.js, fires `forg3d:cart-updated` so header count
 * updates live. No frameworks, no Dawn web components.
 *
 * Also listens for `forg3d:cart-changed` (fired by the cart page) so the two
 * surfaces never show disagreeing totals.
 *
 * Money formatting delegates to assets/forg3d-money.js, which derives the
 * ACTIVE presentment currency's format from Liquid. Do not reintroduce a local
 * '$' fallback here — that was the 2026-08-23 EUR bug (Chatwoot conv. 214).
 */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-forg3d-cart-drawer]');
  if (!drawer) return;

  var body = drawer.querySelector('[data-forg3d-cart-body]');
  var subtotalEl = drawer.querySelector('[data-forg3d-cart-subtotal]');
  var shipEl = drawer.querySelector('[data-forg3d-cart-ship]');
  var checkoutEl = drawer.querySelector('[data-forg3d-cart-checkout]');

  var freeShipCents = parseInt(drawer.getAttribute('data-free-ship-cents'), 10) || 0;
  var showShipNudge = drawer.getAttribute('data-show-ship-nudge') === 'true';

  /* -------------------- open / close -------------------- */

  function openDrawer() {
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    var closeBtn = drawer.querySelector('.forg3d-cart-drawer__close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  function closeDrawer() {
    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  document.addEventListener('forg3d:cart-open', openDrawer);

  /* Cart mutated by another surface (cart page, add-on picker). Re-render so
     the drawer's totals never disagree with the page's. `render` dispatches
     forg3d:cart-updated, which those surfaces consume WITHOUT re-dispatching
     forg3d:cart-changed — so this converges instead of looping. */
  document.addEventListener('forg3d:cart-changed', function () {
    fetchCart().then(render).catch(function () {});
  });

  drawer.querySelectorAll('[data-forg3d-cart-close]').forEach(function (el) {
    el.addEventListener('click', closeDrawer);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
  });

  /* -------------------- formatting -------------------- */

  function formatMoney(cents) {
    // Single source of truth: assets/forg3d-money.js (presentment-currency aware).
    // The old inline fallback here hardcoded '$' and was the ONLY reachable
    // path on this store, so EUR carts rendered as "$149.85" (Chatwoot 214).
    if (window.FORG3DMoney) { return window.FORG3DMoney.format(cents); }
    var iso = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    return (iso ? iso + ' ' : '') + (Number(cents) / 100).toFixed(2);
  }

  /* The subtotal's first paint is Liquid `money_without_trailing_zeros`, so its
     re-render must use the matching form or a whole total visibly changes shape
     ($168 -> $224.00) on the first quantity click. Line prices use plain
     `money` in Liquid and so keep formatMoney above. */
  function formatMoneySubtotal(cents) {
    if (window.FORG3DMoney) { return window.FORG3DMoney.compact(cents); }
    return formatMoney(cents);
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* -------------------- rendering -------------------- */

  var BLADE = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true"><path d="M12 2.4L12 4.1L18.79 5.21L17.59 6.41L21.6 12L19.9 12L18.79 18.79L17.59 17.59L12 21.6L12 19.9L5.21 18.79L6.41 17.59L2.4 12L4.1 12L5.21 5.21L6.41 6.41L12 2.4Z"></path><path d="M12 8.4v7.2M8.4 12h7.2"></path></svg>';
  var CLOSE = '<svg viewBox="0 0 24 24" fill="none" width="14" height="14" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.75" stroke-linecap="square"/></svg>';

  var EMPTY_HTML = ''
    + '<div class="forg3d-cart-drawer__empty">'
    + '<p class="forg3d-cart-drawer__empty-lead">Nothing in the cart yet.</p>'
    + '<p class="forg3d-cart-drawer__empty-sub">Start with a workholding system, or pick up where you left off in your cut list.</p>'
    + '<a class="forg3d-cart-drawer__empty-shop" href="/collections/sys-grp">Shop SYS-GRP</a>'
    + '<a class="forg3d-cart-drawer__empty-cutlist" href="https://portal.forg3d.co/account">' + BLADE + ' View Your Cut List</a>'
    + '</div>';

  function renderItem(item) {
    var img = item.image
      ? '<img src="' + escapeHtml(item.image.replace(/(\.\w{3,4})($|\?)/, '_128x128$1$2'))
        + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy" />'
      : '';
    var variant = (!item.product_has_only_default_variant && item.variant_title)
      ? '<div class="forg3d-cart-drawer__variant">' + escapeHtml(item.variant_title) + '</div>' : '';
    var sku = item.sku ? escapeHtml(item.sku) : '';
    var meta = (sku ? sku + ' · ' : '') + formatMoney(item.original_price) + ' ea';

    return ''
      + '<article class="forg3d-cart-drawer__item" data-line-key="' + escapeHtml(item.key) + '" data-variant-id="' + item.variant_id + '">'
      + '<a href="' + escapeHtml(item.url) + '" class="forg3d-cart-drawer__img" tabindex="-1" aria-hidden="true">' + img + '</a>'
      + '<div class="forg3d-cart-drawer__details">'
      + '<div class="forg3d-cart-drawer__titlerow">'
      + '<a href="' + escapeHtml(item.url) + '" class="forg3d-cart-drawer__name">' + escapeHtml(item.product_title) + '</a>'
      + '<button type="button" class="forg3d-cart-drawer__close-line" data-forg3d-line-remove aria-label="Remove">' + CLOSE + '</button>'
      + '</div>'
      + variant
      + '<div class="forg3d-cart-drawer__meta">' + meta + '</div>'
      + '<div class="forg3d-cart-drawer__row">'
      + '<div class="forg3d-cart-drawer__qty" data-forg3d-qty-wrap>'
      + '<button type="button" data-forg3d-qty-dec aria-label="Decrease quantity">−</button>'
      + '<span data-forg3d-qty-val>' + item.quantity + '</span>'
      + '<button type="button" data-forg3d-qty-inc aria-label="Increase quantity">+</button>'
      + '</div>'
      + '<div class="forg3d-cart-drawer__price" data-forg3d-line-price>' + formatMoney(item.final_line_price) + '</div>'
      + '</div>'
      + '<button type="button" class="forg3d-cart-drawer__cutlist" data-forg3d-line-cutlist'
      + ' data-variant-id="' + item.variant_id + '" data-product-id="' + item.product_id + '"'
      + ' data-handle="' + escapeHtml(item.handle) + '" data-sku="' + escapeHtml(item.sku || '') + '">' + BLADE + ' Cut List</button>'
      + '</div>'
      + '</article>';
  }

  function render(cart) {
    // Body
    if (cart.item_count === 0) {
      body.innerHTML = EMPTY_HTML;
    } else {
      body.innerHTML = cart.items.filter(function (it) {
        return !(it.properties && it.properties._addon_for);
      }).map(renderItem).join('');
    }

    // Add-On Picker: inject the picker link/panel + nested add-on child lines.
    // Additive and reversible — a no-op if forg3d-addon-picker.js is absent.
    if (window.FORG3DAddonDrawer && typeof window.FORG3DAddonDrawer.decorate === 'function') {
      window.FORG3DAddonDrawer.decorate(cart, body);
    }

    // Totals
    if (subtotalEl) subtotalEl.textContent = formatMoneySubtotal(cart.total_price);

    // Disable checkout when empty
    if (checkoutEl) {
      if (cart.item_count === 0) {
        checkoutEl.setAttribute('aria-disabled', 'true');
        checkoutEl.style.pointerEvents = 'none';
        checkoutEl.style.opacity = '0.4';
      } else {
        checkoutEl.removeAttribute('aria-disabled');
        checkoutEl.style.pointerEvents = '';
        checkoutEl.style.opacity = '';
      }
    }

    bindLineButtons();

    // Notify rest of the storefront (header count, etc.)
    document.dispatchEvent(new CustomEvent('forg3d:cart-updated', {
      detail: { item_count: cart.item_count, total_price: cart.total_price }
    }));
  }

  /* -------------------- mutations -------------------- */

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function changeQty(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { return r.json(); });
  }

  function bindLineButtons() {
    body.querySelectorAll('[data-forg3d-line-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('[data-line-key]');
        if (!item) return;
        changeQty(item.getAttribute('data-line-key'), 0).then(render);
      });
    });
    body.querySelectorAll('[data-forg3d-qty-dec]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('[data-line-key]');
        if (!item) return;
        var valEl = item.querySelector('[data-forg3d-qty-val]');
        var current = parseInt(valEl.textContent, 10) || 0;
        var next = Math.max(0, current - 1);
        changeQty(item.getAttribute('data-line-key'), next).then(render);
      });
    });
    body.querySelectorAll('[data-forg3d-qty-inc]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('[data-line-key]');
        if (!item) return;
        var valEl = item.querySelector('[data-forg3d-qty-val]');
        var current = parseInt(valEl.textContent, 10) || 0;
        changeQty(item.getAttribute('data-line-key'), current + 1).then(render);
      });
    });
    body.querySelectorAll('[data-forg3d-line-cutlist]').forEach(function (btn) {
      btn.addEventListener('click', function () { saveToCutList(btn); });
    });
  }

  /* -------------------- cut list from a drawer line -------------------- */
  // Save the variant to the portal Cut List, then remove the line (v1). Anonymous
  // savers reveal the shared email capture. Mirrors the cart page + PDP.
  var PORTAL = 'https://portal.forg3d.co';
  var captureEl = drawer.querySelector('[data-forg3d-cutlist-capture]');
  var pendingSave = null;
  function showCapture() {
    if (!captureEl) return;
    captureEl.hidden = false;
    var i = captureEl.querySelector('[data-forg3d-cutlist-email]');
    if (i) i.focus();
  }
  function saveToCutList(btn) {
    var row = btn.closest('[data-line-key]');
    postSaveDrawer({
      variant_id: btn.getAttribute('data-variant-id'),
      product_id: btn.getAttribute('data-product-id'),
      handle: btn.getAttribute('data-handle'),
      sku: btn.getAttribute('data-sku'),
      quantity: 1
    }, row.getAttribute('data-line-key'));
  }
  function postSaveDrawer(payload, lineKey) {
    return fetch(PORTAL + '/api/cut-list', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
    }).then(function (res) {
      if (res.status === 400) {
        return res.json().catch(function () { return {}; }).then(function (j) {
          if (j && j.error === 'email_required') { pendingSave = { payload: payload, lineKey: lineKey }; showCapture(); return null; }
          throw new Error('bad');
        });
      }
      if (!res.ok) throw new Error('http');
      try {
        var s = JSON.parse(localStorage.getItem('forg3d_cutlist_saved_variants') || '[]');
        if (s.indexOf(String(payload.variant_id)) === -1) { s.push(String(payload.variant_id)); localStorage.setItem('forg3d_cutlist_saved_variants', JSON.stringify(s)); }
      } catch (e) {}
      return changeQty(lineKey, 0).then(render);
    }).catch(function () {});
  }
  (function () {
    var form = drawer.querySelector('[data-forg3d-cutlist-capture-form]');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!pendingSave) return;
      var email = (drawer.querySelector('[data-forg3d-cutlist-email]').value || '').trim();
      if (!email) return;
      pendingSave.payload.email = email;
      pendingSave.payload.consent = !!drawer.querySelector('[data-forg3d-cutlist-consent]').checked;
      var ps = pendingSave; pendingSave = null; if (captureEl) captureEl.hidden = true;
      postSaveDrawer(ps.payload, ps.lineKey);
    });
  })();

  /* -------------------- intercept add-to-cart submits -------------------- */

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action) return;

    // Match standard Shopify add-to-cart endpoints
    var addAction = form.action.replace(/\?.*$/, '');
    var isAddForm = /\/cart\/add$/.test(addAction)
      || form.getAttribute('data-forg3d-cart-add') !== null;

    if (!isAddForm) return;
    if (form.dataset.forg3dCartSkip === 'true') return;

    e.preventDefault();

    var formData = new FormData(form);
    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) {
            console.error('[forg3d-cart] add failed', err);
            throw err;
          });
        }
        return r.json();
      })
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        render(cart);
        openDrawer();
      })
      .catch(function (err) {
        // Surface error visibly — fall back to letting the user retry.
        // Could be extended to show a toast in the drawer head.
        console.error('[forg3d-cart] error', err);
      });
  });

  /* -------------------- initial bind -------------------- */

  bindLineButtons();

  // Public refresh hook so the Add-On Picker can re-render the drawer in place
  // (re-fetch cart → render → decorate) instead of reloading the page.
  window.FORG3DCart = {
    refresh: function () { return fetchCart().then(render); }
  };

  // Decorate the server-rendered body present on first paint (before any mutation).
  if (window.FORG3DAddonDrawer && typeof window.FORG3DAddonDrawer.decorate === 'function') {
    fetchCart().then(function (cart) { window.FORG3DAddonDrawer.decorate(cart, body); }).catch(function () {});
  }
})();
